import { Box, Theme, Typography } from "@outschool/backpack";
import { ParentFragmentFragment } from "@outschool/gql-frontend-generated";
import { useTranslation } from "@outschool/localization";
import {
  addParamsToUrl,
  createUrlWithActivity,
  searchPath,
} from "@outschool/routes";
import { useSession } from "@outschool/ui-auth";
import { TrackedButton } from "@outschool/ui-components-shared";
import React, { useEffect, useMemo } from "react";

import * as GirlWithFlowerPNG from "../../../../images/girl-with-flower.png";
import { useEnrollmentHistoryQuery } from "../../../queries/EnrollmentHistoryQuery";
import { ActivityListing } from "../../listings/ActivityListing";
import { EmptyProfileCard } from "../EmptyProfileCard";
import { Give20Get20ClassShare } from "../Give20Get20";
import { PrivacyDisclaimerText } from "../PrivacyDisclaimerText";
import {
  ActivityReviewActions,
  useActivityReviewEligibility,
} from "./ActivityReviewActions";

export default function ParentClassHistoryPaginated({
  parent,
  isCurrentUsersProfile,
  profileLinkUid,
  privacyState,
  privateLink,
  publicView,
}: {
  parent: ParentFragmentFragment;
  isCurrentUsersProfile: boolean;
  profileLinkUid?: string;
  privacyState?: string | null;
  privateLink: string;
  publicView: boolean;
}) {
  const { t } = useTranslation("client\\routes\\ParentProfile");
  const { currentUser } = useSession();
  const [showShareModalAtIndex, setShowShareModalAtIndex] = React.useState<
    null | number
  >(null);
  const { data, fetchMoreEnrollmentHistory, isFetchingMore, hasMore, loading } =
    useEnrollmentHistoryQuery({
      variables: {
        uid: parent.uid,
        skipRating: false,
        profileLinkUid,
        publicView,
      },
    });
  const reviewMap = useActivityReviewEligibility({ isCurrentUsersProfile });

  useEffect(() => {
    fetchMoreEnrollmentHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { activity, activityWithLeader } =
    useMemo(() => {
      if (showShareModalAtIndex === null) {
        return;
      }

      const listings = data?.pastEnrollmentsForParentProfile;

      if (!listings) {
        return;
      }

      const activity = listings[showShareModalAtIndex]?.activity;
      if (!activity) {
        return;
      }

      const activityWithLeader = !listings[showShareModalAtIndex]?.leader
        ? activity
        : {
            ...activity,
            leader: listings[showShareModalAtIndex]?.leader,
          };

      return { activity, activityWithLeader };
    }, [data?.pastEnrollmentsForParentProfile, showShareModalAtIndex]) || {};

  const shareLink = useMemo(() => {
    if (!activity) {
      return;
    }

    return addParamsToUrl(createUrlWithActivity(activity), {
      signup: true,
      usid: currentUser?.slug_id,
    });
  }, [currentUser, activity]);

  const onClose = useMemo(() => {
    return () => setShowShareModalAtIndex(null);
  }, [setShowShareModalAtIndex]);

  if (
    !loading &&
    !isFetchingMore &&
    !isCurrentUsersProfile &&
    !data?.pastEnrollmentsForParentProfile.length
  ) {
    return null;
  }

  const showHasMore =
    hasMore && data?.pastEnrollmentsForParentProfile.length !== 0;

  return (
    <Box
      sx={{
        width: "100%",
        mb: "64px",
      }}
    >
      <Box
        sx={(theme: Theme) => ({
          mx: "1em",
          mb: "1rem",
          [theme.breakpoints.down("sm")]: { mx: 0 },
        })}
      >
        <Typography emphasized variant={"h6"}>
          {t("My Recent Classes")}
        </Typography>
        {isCurrentUsersProfile && (
          <PrivacyDisclaimerText
            privacyState={privacyState}
            privateLink={privateLink}
          />
        )}
      </Box>
      {data?.pastEnrollmentsForParentProfile.map(
        (listing: $TSFixMe, activityIndex: $TSFixMe) => {
          if (!listing) {
            return null;
          }
          return (
            <ActivityListing
              key={listing.activity.uid}
              listing={listing}
              openInNewTab
              trackingProperties={{
                order: activityIndex,
                activity_title: listing.activity.title,
              }}
              listingIndex={activityIndex}
              listingDetailsOverride={
                // Show review actions if looking at your own profile
                isCurrentUsersProfile && (
                  <ActivityReviewActions
                    listing={listing}
                    shareOnClick={(e: React.SyntheticEvent) => {
                      e.preventDefault(); // stops the browser from following the link
                      e.stopPropagation(); // stops the event from bubbling to <a>
                      setShowShareModalAtIndex(activityIndex);
                    }}
                    reviewItem={reviewMap.get(listing.activity.uid) || {}}
                  />
                )
              }
            />
          );
        },
      )}
      {!data?.pastEnrollmentsForParentProfile.length &&
        isCurrentUsersProfile && (
          <EmptyProfileCard
            imageUrl={GirlWithFlowerPNG}
            topText={t("You haven’t taken any classes yet!")}
            bottomText={t("Discover classes for every learning need...")}
            buttonText={t("Find my next class")}
            buttonTo={searchPath()}
          />
        )}
      <Give20Get20ClassShare
        shareLink={shareLink}
        onClose={onClose}
        usesOutReachReferral={
          !!currentUser?.teacher_approved_at && !currentUser?.suspended_at
        }
        activity={activityWithLeader}
      />
      {showHasMore && (
        <ShowMoreButton
          parentUid={parent.uid}
          fetchMore={fetchMoreEnrollmentHistory}
          isFetchingMore={isFetchingMore}
        />
      )}
    </Box>
  );
}

function ShowMoreButton({
  parentUid,
  fetchMore,
  isFetchingMore,
}: {
  parentUid: string;
  fetchMore: () => Promise<void>;
  isFetchingMore: boolean;
}) {
  const { t } = useTranslation("client\\routes\\ParentProfile");
  return (
    <Box>
      <TrackedButton
        variant={"outlined"}
        trackingName="parent_profile_show_more"
        trackingUniqueId={`parent_profile_show_more-parentuid-${parentUid}`}
        disabled={isFetchingMore}
        sx={(theme: Theme) => ({
          display: "flex",
          margin: "auto",
          [theme.breakpoints.down("md")]: {
            marginTop: "1em",
            marginBottom: "1em",
          },
          whiteSpace: "unset",
        })}
        onClick={fetchMore}
      >
        {t("Show More")}
      </TrackedButton>
    </Box>
  );
}
